<template>
    <div class="product-card">
        <div data-name="product-card-header">
            <div v-if="data.crosspromoter" class="badge">
                <Icon v-b-tooltip.hover title="Active Crosspromoter" icon="badge" class="active-cp-badge"/>
            </div>
            <Thumbnail 
                size = "medium"
                :data = "data" />
            <div 
                @click="toPartnerPage(data.id)"
                v-if="$route.params.campaignType === 'publish'" 
                class="overlay">
                    <p>View More</p>
            </div>
            <div data-name="card-props">
                <div class="d-flex align-items-center">
                    <a 
                        :href="data.project_url" 
                        target="_blank" 
                        style="z-index: 100" 
                        @click="toProject"
                        class="ks-link">
                        <Icon icon="kickstarter"/>
                    </a>
                    <div v-if="status || data.indemand" data-name="product-status">
                        <Icon :icon="data.indemand ? 'in-demand-status' : 'live-status'"/>
                        <p class="text-capitalize">{{data.indemand ? 'InDemand' : data.state}}</p>
                    </div>
                </div>
                <div data-name="backers">
                    <p>backers</p>
                    <p>{{data.backers}}</p>
                </div>
            </div>
        </div>
        <div data-name="product-card-body" class="line-clamp">
            <p>{{data.title}}</p>
            <p>{{data.description}}</p>
        </div>
        <div data-name="product-card-footer" class="mt-auto d-flex align-items-center justify-content-between">
            <router-link 
                v-if="$route.params.campaignType === 'publish'"
                @click.native="trackEvent('published', data)"
                :to="{path: '/edit-campaign', query: {id: data.id, model: 'campaigns'}}"
                class="w-100 edit-info-published">
                    <Button type="editInfo">
                        Edit Info
                    </Button>
            </router-link>
            <router-link 
                v-if="$route.params.campaignType === 'drafts'"
                @click.native="trackEvent('draft', data)"
                :to="{path:'/wizard/edit-campaign', query: {from: $route.fullPath, id: data.id, indrafts: true, model: 'drafts'}}"
                class="w-100 edit-info-draft">
                    <Button type="editInfo">
                        Edit Info
                    </Button>
            </router-link>
            <Button 
                v-if="$route.params.campaignType === 'publish'"
                :disabled = "loading"
                class="add-to-archive"
                @click.native="archiveCampaign(data)">
                    <Icon icon="archive-icon" class="ml-3"/>
            </Button>
            <Button
                type="editInfo" 
                class="restore-campaign"
                v-if="$route.params.campaignType === 'archives'"
                @click.native="restoreCampaign(data)">
                    Restore
            </Button>
        </div>
        <BeforeArchive/>
    </div>
    
</template>

<script>
import { ref, computed } from '@vue/composition-api'
import {elasticQuery, ELASTIC_SEARCH_CP_REQUESTS_INDEX} from '../../../firebase'
import {deleteData, addData} from '../../../firebaseCRUD'
import { useRouter } from '../../../core/hooks'
import {message} from 'ant-design-vue'
import BeforeArchive from '../../../popups/BeforeArchive'
import Icon from '../../../components/Theme/Icon'
import Thumbnail from '../../../components/Theme/Thumbnail'
import { Favorite } from '../../../components/Icons'
import Button from './Button'
export default {
    name: 'CampaignCard',
    components: {
        Button,
        Icon,
        Favorite,
        BeforeArchive,
        Thumbnail
    },
    props: {
        data: Object,
        loading: Boolean
    },
    setup(props, {emit, root}) {

        const {route, router} = useRouter()
        const status = computed(() =>{
            switch(props.data.state) {
                case 'live':
                    return 'live-status'
                case 'upcoming':
                    return 'upcoming-status'
            }
        })

        const toProject = () => {
            let campaignType = 'Published'
            switch(route.value.params.campaignType) {
                case 'archives':
                    campaignType = 'Archive'
                    break
                case 'drafts':
                    campaignType = 'Drafts'
                    break
            }
            root.$sendMixpanelEvent('KS Icon Clicked', {
                'KS Icon Location': `Dashboard ${campaignType}`,
                'Viewed Campaign': props.data.title
            });
            root.$ga.event('Campaign', 'KS Icon Click', `
                Location: Dashboard ${campaignType},
                Campaign: ${props.data.title}
            `)
        }

        const trackEvent = (campaignType, campaign) => {
            if(campaignType === 'published') {
                root.$sendMixpanelEvent('Edit Campaign Opened', {
                    'Opened From': 'Published Campaigns',
                    "Campaign Name": campaign.title
                })

                root.$ga.event('Campaign', 'Edit Campaign Open', `
                    OpenedFrom: Published Campaigns,
                    Campaign: ${campaign.title}
                `)
            } else {
                root.$sendMixpanelEvent('Dashboard Draft Edit Button Clicked', {
                    "Campaign Name": campaign.title
                })
                root.$ga.event('Campaign', 'Draft Edit Click', 'Campaign', campaign.title)
            }
        }

        const archiveCampaign = (data) => {
            emit('archive', data)
        }
        const restoreCampaign = (data) => {
            emit('restore', data)
        }
        const toPartnerPage = (id) => {
            router.push({
                path: `/campaigns/${id}`
            })
            .catch(() => {})
        }
        return {
            archiveCampaign,
            restoreCampaign,
            toPartnerPage,
            status,
            trackEvent,
            toProject
        }
    },
}
</script>

<style lang="scss" scoped>
@import '../../../assets/scss/variables.scss';
div.product-card {
    height: 500px;
    padding: 16px;
    background: $c14;
    box-shadow: 0px 0px 35px rgba(154, 161, 171, 0.15);
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    margin-bottom: 24px;
    p {
        padding-bottom: 0 !important;
        margin-bottom: 0 !important;
    }
    div[data-name="product-card-header"] {
        height: 288px;
        position: relative;
        border-radius: 3px;
        overflow: hidden;
        div.badge {
            position: absolute;
            z-index: 100;
            top: 8px;
            left: 8px;
            transform: scale(1.2);
            transition: all 0.2s ease;
            &:hover {
                transform: scale(1.3);
            }
        }
        img {
            height: 100%;
            object-fit:cover;
        }
        div.overlay {
            display: none;
            width: 100%;
            height: 100%;
            position: absolute;
            top: 0;
            left: 0;
            background: none;
            transition: all 0.2s ease;
            z-index: 99;
            cursor: pointer;
            p {
                display: none;
                color: $c14;
                font-size: $p2;
                font-weight: $weight_bold;
                cursor: pointer;
            }

        }
        &:hover {
            div.overlay {
                background: #00000080;
                display: flex;
                justify-content: center;
                align-items: center;
                p {
                    display: block;
                }
            }
        }
        div[data-name="card-props"] {
            width: 100%;
            height: 48px;
            bottom: 0;
            background: #15202be6;
            position: absolute;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-top: 10px;
            padding-bottom: 10px;
            padding-left: 8px;
            padding-right: 8px;

            div[data-name="product-status"] {
                font-size: $p5;
                font-weight: 300;
                color: $c14;
                display: flex;
                align-items: center;
                margin-left: 10px;

                p {
                    margin-left: 6px;
                }
            }
            div[data-name="backers"] {
                text-align: center;
                p {
                    font-size: $p5;
                    font-weight: 300;
                    color: $c14;
                    &:last-child {
                        font-size: $p3;
                        font-weight: $weight_bold;
                    }
                }
            }
        }
    }
    div[data-name="product-card-body"] {
        overflow: hidden;
        text-overflow:ellipsis;
        padding-top: 16px;

        &.line-clamp {
            display: -webkit-box;
            -webkit-line-clamp: 4;
            -webkit-box-orient: vertical;  
        }
        
        p {
            &:first-child {
                font-size: $p3;
                color: $c6o9;
                font-weight: $weight_bold;
            }
            &:last-child {
                font-size: $p4;
                color: $c6o9;
                margin-top: 8px;
            }
        }
    }
    div[data-name="product-card-footer"] {
        padding-top: 48px;
    }
}
</style>